import React, { RefObject } from "react";
import WarningBanner from "@/components/ui/WarningBanner";
import AddressAutocomplete from "@/components/ui/AddressAutoComplete";
import Button from "@/components/ui/Button";
import CloseIcon from "@/assets/img/CloseIcon";
import CookieCheck from "@/utils/helpers/CookieCheck";
import visaLogo from "@/assets/img/Visa.png";
import mastercardLogo from "@/assets/img/mc.png";
import amexLogo from "@/assets/img/amex.png";
import discoverLogo from "@/assets/img/discover.png";
import { CVVInfoModal, BillingInfoModal } from "../modals";
import { Address } from "@/types/branch";

interface CardData {
  name: string;
  cvv: string;
  month: string;
  year: string;
}

interface Errors {
  name: string;
  cvv: string;
  month: string;
  year: string;
  phone: string;
}

interface PaymentFormViewProps {
  error: string;
  loading: boolean;
  orderSuccess: boolean;
  cardData: CardData;
  errors: Errors;
  phone: string;
  address: Address;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  tokenInputRef: RefObject<HTMLInputElement | null>;
  isCVVModalOpen: boolean;
  isBillingModalOpen: boolean;
  onClose: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressChange: (field: keyof Address, value: string) => void;
  onAddressPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  onPayNow: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  openCVVModal: () => void;
  closeCVVModal: () => void;
  openBillingModal: () => void;
  closeBillingModal: () => void;
}

export const PaymentFormView: React.FC<PaymentFormViewProps> = ({
  error,
  loading,
  orderSuccess,
  cardData,
  errors,
  phone,
  address,
  iframeRef,
  tokenInputRef,
  isCVVModalOpen,
  isBillingModalOpen,
  onClose,
  onInputChange,
  onAddressChange,
  onAddressPlaceSelected,
  onPayNow,
  openCVVModal,
  closeCVVModal,
  openBillingModal,
  closeBillingModal,
}) => {
  return (
    <div id="payment-form" className="payment-form p-4">
      {error && (
        <WarningBanner title="Payment Error" text={error} className="mt-4" />
      )}
      <>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base sm:text-2xl font-bold ml-2">
            Card Information
          </h2>
          <button onClick={onClose} className="text-black">
            <CloseIcon />
          </button>
        </div>
        <CookieCheck />
        <div className="w-full flex justify-center">
          <form name="tokenform" id="tokenform">
            <div className="md:flex md:flex-row md:gap-20 md:px-20 md:mt-20">
              <div className="md:flex-1">
                <div className="md:w-full space-y-4">
                  <div className="flex justify-between items-center">
                    <label
                      htmlFor="card Number"
                      className="text-sm font-medium text-gray-700"
                    >
                      Card Number
                    </label>
                    <div className="flex space-x-1">
                      <img src={visaLogo} alt="Visa" className="w-6 h-4" />
                      <img
                        src={mastercardLogo}
                        alt="Mastercard"
                        className="w-6 h-4"
                      />
                      <img src={amexLogo} alt="Amex" className="w-6 h-4" />
                      <img
                        src={discoverLogo}
                        alt="Discover"
                        className="w-6 h-4"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <div
                      className="w-full h-14 px-3 py-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200 flex items-center justify-center overflow-hidden"
                      style={{ height: "55px" }}
                    >
                      <iframe
                        title="Token Frame"
                        ref={iframeRef}
                        id="tokenFrame"
                        name="tokenFrame"
                        src="https://fts.cardconnect.com/itoke/ajax-tokenizer.html?css=%2Eerror%7Bcolor%3A%20red%3B%7Dinput%7Bwidth%3A280px%3B%7Dinput%7Bheight%3A30px%3B%7D"
                        className="w-full h-full"
                        style={{
                          border: "none",
                          outline: "none",
                          position: "absolute",
                          overflow: "hidden",
                          background: "transparent",
                        }}
                      />
                    </div>
                  </div>
                  <input
                    type="hidden"
                    ref={tokenInputRef}
                    name="token"
                    id="tokenInput"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Cardholder Name
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Type name here"
                        value={cardData.name}
                        onChange={onInputChange}
                      />
                      {errors.name && (
                        <div className="text-red-500 text-sm">
                          {errors.name}
                        </div>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="cvv"
                        className="block text-sm font-medium text-gray-700"
                      >
                        CVV
                        <span className="text-red-600">*</span>
                        <span
                          onClick={openCVVModal}
                          className="ml-1 cursor-pointer"
                        >
                          <svg
                            className="w-4 h-4 inline-block"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-4a1 1 0 100 2 1 1 0 000-2zm1 4a1 1 0 10-2 0v4a1 1 0 102 0v-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        name="cvv"
                        id="cvv"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="xxx"
                        value={cardData.cvv}
                        onChange={onInputChange}
                      />
                      {errors.cvv && (
                        <div className="text-red-500 text-sm">{errors.cvv}</div>
                      )}
                      <CVVInfoModal
                        isOpen={isCVVModalOpen}
                        onClose={closeCVVModal}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="month"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Expiration Month
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={1}
                        max={12}
                        placeholder="MM"
                        name="month"
                        id="month"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        value={cardData.month}
                        onChange={onInputChange}
                      />
                      {errors.month && (
                        <div className="text-red-500 text-sm">
                          {errors.month}
                        </div>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="year"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Expiration Year
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        name="year"
                        id="year"
                        placeholder="YYYY"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        value={cardData.year}
                        onChange={onInputChange}
                      />
                      {errors.year && (
                        <div className="text-red-500 text-sm">
                          {errors.year}
                        </div>
                      )}
                    </div>
                  </div>
                  <h2 className="text-xl font-bold mb-4">
                    Billing Address
                    <span
                      onClick={openBillingModal}
                      className="ml-1 cursor-pointer"
                    >
                      <svg
                        className="w-4 h-4 inline-block"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-4a1 1 0 100 2 1 1 0 000-2zm1 4a1 1 0 10-2 0v4a1 1 0 102 0v-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </h2>

                  <BillingInfoModal
                    isOpen={isBillingModalOpen}
                    onClose={closeBillingModal}
                  />
                  <AddressAutocomplete
                    value={address.street ?? ""}
                    onChange={(value: string) =>
                      onAddressChange("street", value)
                    }
                    onPlaceSelected={onAddressPlaceSelected}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="city"
                        className="block text-sm font-medium text-gray-700"
                      >
                        City
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        id="city"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Type city here"
                        value={address.city}
                        onChange={(e) =>
                          onAddressChange("city", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="region"
                        className="block text-sm font-medium text-gray-700"
                      >
                        State
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="region"
                        id="region"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Type state here"
                        value={address.province}
                        onChange={(e) =>
                          onAddressChange("province", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="country"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Country
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        id="country"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Type country here"
                        value={address.country}
                        onChange={(e) =>
                          onAddressChange("country", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="postal"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Zip Code
                        <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="postal"
                        id="postal"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Type zip code here"
                        value={address.postal_code}
                        onChange={(e) =>
                          onAddressChange("postal_code", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:flex-1">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Phone Number
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 flex items-center">
                      <span className="text-gray-500 sm:text-sm border-r border-gray-300 bg-gray-100 px-3 py-2 h-full">
                        +1
                      </span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      className="block w-full pl-16 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="(xxx) xxx-xxxx"
                      value={phone.replace(
                        /(\d{3})(\d{3})(\d{4})/,
                        "($1) $2-$3",
                      )}
                      onChange={onInputChange}
                      maxLength={14}
                      required
                    />
                  </div>
                  {errors.phone && (
                    <div className="text-red-500 text-sm">{errors.phone}</div>
                  )}
                </div>
                <div className="text-sm text-gray-900">
                  Please enter your 10-digit US phone number to receive order
                  confirmation. By entering your number, you consent to receive
                  SMS from Redbird, including marketing messages sent by Redbird
                  on behalf of the merchant. You can opt out at any time by
                  replying with 'STOP' or following other opt-out instructions
                  provided in the messages. Message & data rates may apply.
                  Message frequency varies. Text HELP for support. By
                  proceeding, you agree to our{" "}
                  <a
                    href="https://www.redbirdtab.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700"
                  >
                    terms of service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.redbirdtab.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700"
                  >
                    privacy policy
                  </a>
                  .
                </div>
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    text={loading ? "Processing..." : "Pay Now"}
                    className="w-full mt-4 h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px]
                            text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                    onClick={onPayNow}
                    disabled={loading}
                  />
                </div>
                {error && <div className="text-red-500 mt-4">{error}</div>}

                {orderSuccess && (
                  <div className="bg-green-100 text-green-800 p-4 rounded mt-4">
                    Your order has been successfully placed!
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </>
    </div>
  );
};
